// ── Enums / string unions ──────────────────────────────────────────────────────

export type BankDepositWorkflowStatus =
  | 'Draft'
  | 'Submitted'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'Posted'
  | 'Cancelled';

export type BankDepositPostingStatus = 'NotPosted' | 'Posted' | 'Cancelled';

export type BankDepositType = 'Cash' | 'Cheque' | 'Mixed';

// ── Status display helpers ────────────────────────────────────────────────────

export const BNK_DEPOSIT_WORKFLOW_STATUS_LABELS: Record<BankDepositWorkflowStatus, string> = {
  Draft:          'Draft',
  Submitted:      'Submitted',
  PendingApproval:'Pending Approval',
  Approved:       'Approved',
  Rejected:       'Rejected',
  Posted:         'Posted',
  Cancelled:      'Cancelled',
};

export const BNK_DEPOSIT_WORKFLOW_STATUS_COLORS: Record<BankDepositWorkflowStatus, string> = {
  Draft:          'bg-gray-100 text-gray-700',
  Submitted:      'bg-blue-100 text-blue-700',
  PendingApproval:'bg-yellow-100 text-yellow-700',
  Approved:       'bg-green-100 text-green-700',
  Rejected:       'bg-red-100 text-red-700',
  Posted:         'bg-purple-100 text-purple-700',
  Cancelled:      'bg-gray-200 text-gray-500',
};

// ── Line DTO ──────────────────────────────────────────────────────────────────

export interface BankDepositLineDto {
  id:                   string;
  depositId:            string;
  lineNumber:           number;
  depositSource?:       string | null;
  sourceDocumentNumber?: string | null;
  /** The cash/till GL account this line draws from (GAP-016). */
  sourceGlAccountId?:   string | null;
  sourceGlAccountCode?: string | null;
  sourceGlAccountName?: string | null;
  paymentMethod:        string;
  chequeNumber?:        string | null;
  chequeDate?:          string | null;
  /** The register row this line deposits (GAP-016). Null for cash or a hand-keyed number. */
  chequeRecordId?:      string | null;
  customerId?:          string | null;
  customerCode?:        string | null;
  amount:               number;
  currencyCode:         string;
  exchangeRate:         number;
  baseAmount:           number;
  remarks?:             string | null;
  /** The cash-due (location, date) row this line settles (GAP-016 due-tracking). */
  sourceLocationId?:    string | null;
  sourceBusinessDate?:  string | null;
  /** The cost centre whose cash this line banks (Cash Deposit screen). */
  sourceCostCenterId?:  string | null;
}

/** One location's cash collection for one business date, and what remains un-banked (GAP-016). */
export interface CashDueRowDto {
  locationId:       string;
  locationCode:     string;
  locationName:     string;
  businessDate:     string;
  currencyCode:      string;
  dueAmount:         number;
  depositedAmount:   number;
  remainingAmount:   number;
}

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface BankDepositDto {
  id:                  string;
  companyId:           string;
  locationId?:         string | null;
  costCenterId?:       string | null;
  financialYearCode:   string;
  financialPeriodCode: string;
  depositNumber:       string;
  depositDate:         string;
  postingDate:         string;
  bankAccountId:       string;
  bankAccountCode:     string;
  bankAccountName:     string;
  bankGLAccountId?:    string | null;
  depositType:         BankDepositType;
  currencyCode:        string;
  exchangeRate:        number;
  totalAmount:         number;
  baseAmount:          number;
  reference?:          string | null;
  narration?:          string | null;
  workflowStatus:      BankDepositWorkflowStatus;
  postingStatus:       BankDepositPostingStatus;
  submittedBy?:        string | null;
  submittedDate?:      string | null;
  approvedBy?:         string | null;
  approvedDate?:       string | null;
  rejectedBy?:         string | null;
  rejectedDate?:       string | null;
  rejectionReason?:    string | null;
  postedBy?:           string | null;
  postedDate?:         string | null;
  cancelledBy?:        string | null;
  cancelledDate?:      string | null;
  cancellationReason?: string | null;
  isReversal:          boolean;
  originalDepositId?:  string | null;
  reversalDepositId?:  string | null;
  sourceGlJournalId?:  string | null;
  createdDate:         string;
  lines:               BankDepositLineDto[];
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface BankDepositListDto {
  id:              string;
  depositNumber:   string;
  depositDate:     string;
  bankAccountCode: string;
  bankAccountName: string;
  depositType:     BankDepositType;
  currencyCode:    string;
  totalAmount:     number;
  baseAmount:      number;
  workflowStatus:  BankDepositWorkflowStatus;
  postingStatus:   BankDepositPostingStatus;
  reference?:      string | null;
  createdDate:     string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface BankDepositFilterDto {
  companyId?:          string;
  locationId?:         string;
  costCenterId?:       string;
  financialYearCode?:  string;
  bankAccountId?:      string;
  depositType?:        string;
  workflowStatus?:     string;
  postingStatus?:      string;
  dateFrom?:           string;
  dateTo?:             string;
  search?:             string;
  pageNumber?:         number;
  pageSize?:           number;
  sortBy?:             string;
  sortDescending?:     boolean;
}

// ── Line request ──────────────────────────────────────────────────────────────

export interface BankDepositLineRequest {
  lineNumber:           number;
  depositSource?:       string | null;
  sourceDocumentNumber?: string | null;
  /** The cash/till GL account this line draws from (GAP-016). */
  sourceGlAccountId?:   string | null;
  paymentMethod:        string;
  chequeNumber?:        string | null;
  chequeDate?:          string | null;
  /** The register row this line deposits (GAP-016). Null for cash or a hand-keyed number. */
  chequeRecordId?:      string | null;
  customerId?:          string | null;
  customerCode?:        string | null;
  amount:               number;
  currencyCode:         string;
  exchangeRate:         number;
  remarks?:             string | null;
  /** The cash-due (location, date) row this line settles (GAP-016 due-tracking). Null for a
   *  cheque line, or a cash line raised without picking a due row. */
  sourceLocationId?:    string | null;
  sourceBusinessDate?:  string | null;
  /** The cost centre whose cash this line banks (Cash Deposit screen). Kept on edit. */
  sourceCostCenterId?:  string | null;
}

// ── Create / Update requests ──────────────────────────────────────────────────

export interface CreateBankDepositRequest {
  companyId:           string;
  locationId?:         string | null;
  costCenterId?:       string | null;
  financialYearCode:   string;
  financialPeriodCode: string;
  depositDate:         string;
  postingDate:         string;
  bankAccountId:       string;
  depositType:         BankDepositType;
  currencyCode:        string;
  exchangeRate:        number;
  reference?:          string | null;
  narration?:          string | null;
  lines:               BankDepositLineRequest[];
}

export interface UpdateBankDepositRequest {
  locationId?:       string | null;
  costCenterId?:     string | null;
  depositDate:       string;
  postingDate:       string;
  bankAccountId:     string;
  bankAccountCode:   string;
  bankAccountName:   string;
  bankGLAccountId?:  string | null;
  depositType:       BankDepositType;
  currencyCode:      string;
  exchangeRate:      number;
  reference?:        string | null;
  narration?:        string | null;
  lines:             BankDepositLineRequest[];
}

// ── Cash Deposit screen ───────────────────────────────────────────────────────

/** A cash in hand account a deposit can be made from, with the cost centres mapped to it. */
export interface CashAccountOptionDto {
  id:            string;
  code:          string;
  name:          string;
  costCenterIds: string[];
}

/** One grid row: cash posted to the account for one cost centre on one day. */
export interface CashToDepositRowDto {
  costCenterId:      string;
  costCenterCode:    string;
  costCenterName:    string;
  locationId?:       string | null;
  businessDate:      string;
  currencyCode:      string;
  netAmount:         number;
  /** Posted deposits. */
  depositedAmount:   number;
  /** Saved but not yet posted deposits. They reserve the cash. */
  pendingAmount:     number;
  undepositedAmount: number;
}

/** Unbanked cash for one cost centre, by days since the business date. */
export interface UnbankedCashAgeRowDto {
  costCenterId:       string;
  costCenterCode:     string;
  costCenterName:     string;
  currencyCode:       string;
  days0To1:           number;
  days2To3:           number;
  days4To7:           number;
  days8Plus:          number;
  totalUndeposited:   number;
  pendingAmount:      number;
  oldestBusinessDate?: string | null;
}

export interface CashDepositLineRequest {
  costCenterId: string;
  businessDate: string;
  amount:       number;
}

export interface CreateCashDepositRequest {
  depositDate:    string;
  cashAccountId:  string;
  costCenterId:   string;
  bankAccountId:  string;
  depositSlipNo?: string | null;
  remarks?:       string | null;
  lines:          CashDepositLineRequest[];
}

export interface CashDepositResultDto {
  depositId:     string;
  depositNumber: string;
  status:        string;
  posted:        boolean;
  totalAmount:   number;
  currencyCode:  string;
  message?:      string | null;
}
